import { useEffect, useRef, useState } from "react";
import MuiDialog from "@mui/material/Dialog";
import { IconX, IconPhoto, IconTrash } from "@tabler/icons-react";
import { mutateByPrefix } from "../../../../swr/useApi";
import { Button } from "../../../../components/common/button";
import { Heading, Text } from "../../../../components/common/typography";
import { Input } from "../../../../components/common/input";
import { Select } from "../../../../components/forms/select";
import { api, warrantyClaimsBuyerService } from "../../../api_client";
import { CreateWarrantyClaimDtoClaimTypeEnum } from "../../../../api";
import type { AttachmentInputDto } from "../../../../api";
import { showSuccessToast, showErrorToast } from "../../../../utils/toasts";

const CLAIM_TYPE_OPTIONS = [
  { value: CreateWarrantyClaimDtoClaimTypeEnum.Defect, label: "Defect" },
  { value: CreateWarrantyClaimDtoClaimTypeEnum.Damage, label: "Damage" },
  { value: CreateWarrantyClaimDtoClaimTypeEnum.MissingPart, label: "Missing Part" },
  { value: CreateWarrantyClaimDtoClaimTypeEnum.DoesNotMatchDescription, label: "Does Not Match Description" },
  { value: CreateWarrantyClaimDtoClaimTypeEnum.Other, label: "Other" },
];

const MAX_PHOTOS = 3;
const MAX_FILE_BYTES = 10 * 1024 * 1024;

interface Props {
  open: boolean;
  onClose: () => void;
  saleOrderId: number;
  saleOrderLineId: number;
  productName: string;
}

interface PendingPhoto {
  file: File;
  preview: string;
}

async function uploadPhoto(file: File): Promise<AttachmentInputDto | null> {
  try {
    const formData = new FormData();
    formData.append("file", file, file.name);
    const res = await api.post<{ objectKey: string; fileName: string; contentType: string; fileSize: number }>(
      "/api/v1/warranty-claims/upload-file",
      formData,
    );
    return { objectKey: res.data.objectKey, fileName: res.data.fileName, contentType: res.data.contentType, fileSize: res.data.fileSize };
  } catch (err: unknown) {
    const e = err as { response?: { status?: number }; message?: string };
    console.error("[warranty-upload] upload failed:", e?.response?.status, e?.message);
    return null;
  }
}

export function CreateWarrantyClaimDialog({ open, onClose, saleOrderId, saleOrderLineId, productName }: Props) {
  const [claimType, setClaimType] = useState<string>("");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setClaimType("");
    setDescription("");
    setPhotos([]);
  }, [open]);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (fileRef.current) fileRef.current.value = "";
    const remaining = MAX_PHOTOS - photos.length;
    const toAdd = files.slice(0, remaining).filter((f) => {
      if (f.size > MAX_FILE_BYTES) {
        showErrorToast(`${f.name} exceeds the 10 MB limit.`);
        return false;
      }
      return true;
    });
    const previews = toAdd.map((file) => ({ file, preview: URL.createObjectURL(file) }));
    setPhotos((prev) => [...prev, ...previews]);
  }

  function removePhoto(index: number) {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  }

  async function handleSubmit() {
    if (!claimType || !description.trim()) {
      showErrorToast("Please fill in the claim type and description.");
      return;
    }
    setSubmitting(true);
    try {
      const attachments: AttachmentInputDto[] = [];
      for (const p of photos) {
        const att = await uploadPhoto(p.file);
        if (!att) {
          showErrorToast("Failed to upload one or more photos. Please try again.");
          return;
        }
        attachments.push(att);
      }
      await warrantyClaimsBuyerService.createWarrantyClaim({
        saleOrderId,
        saleOrderLineId,
        claimType: claimType as CreateWarrantyClaimDtoClaimTypeEnum,
        description: description.trim(),
        ...(attachments.length > 0 ? { attachments } : {}),
      });
      showSuccessToast("Warranty claim submitted successfully.");
      void mutateByPrefix("buyer-warranty-claims");
      onClose();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      const flat = Array.isArray(msg) ? msg.join(", ") : msg;
      showErrorToast(flat || "Failed to submit warranty claim.");
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = !!claimType && description.trim().length > 0 && !submitting;

  return (
    <MuiDialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      slotProps={{
        paper: {
          className: "!rounded-2xl !max-w-xl !shadow-xl",
          sx: { backgroundColor: "var(--color-bg-default)", backgroundImage: "none" },
        },
      }}
    >
      <div className="flex items-start justify-between border-b border-borderDefault px-6 py-4">
        <div>
          <Heading variant="h3" tone="dark">File a Warranty Claim</Heading>
          <Text variant="bodySm" tone="muted" className="mt-0.5 line-clamp-1">{productName}</Text>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          aria-label="Close"
          className="text-textMuted hover:text-textDark transition-colors"
        >
          <IconX size={20} />
        </button>
      </div>

      <div className="flex flex-col gap-4 px-6 py-5">
        <Select
          name="claimType"
          label="Claim type"
          placeholder="Select a type"
          value={claimType}
          onChange={(e) => setClaimType(e.target.value)}
          options={CLAIM_TYPE_OPTIONS}
        />

        <Input
          label="Description"
          multiline
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the issue in detail…"
        />

        <div className="flex flex-col gap-2">
          <Text variant="bodyBold" tone="dark">Photos (optional, up to {MAX_PHOTOS})</Text>
          <div className="flex flex-wrap gap-2">
            {photos.map((p, i) => (
              <div key={i} className="relative h-20 w-20 shrink-0">
                <img
                  src={p.preview}
                  alt={`Photo ${i + 1}`}
                  className="h-full w-full rounded-xl object-cover border border-borderDefault"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-textDark text-bgWhite"
                  aria-label="Remove photo"
                >
                  <IconTrash size={11} />
                </button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-dashed border-borderDark bg-bgLight text-textMuted hover:border-brandPrimary hover:text-brandPrimary transition-colors"
              >
                <IconPhoto size={22} />
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-borderDefault px-6 py-4">
        <Button variant="secondary" size="md" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="filledDark" size="md" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? "Submitting…" : "Submit Claim"}
        </Button>
      </div>
    </MuiDialog>
  );
}
